import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, BellRing, Receipt, ChefHat, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, BACKEND } from "../../lib/api";
import { useLive } from "../../lib/live";
import { STATUS_LABEL } from "../../components/VegMark";
import { GuestError } from "./Menu";
import { CUSTOMER_ONLINE_PAYMENTS_ENABLED } from '../../lib/customerPayments';

const STEPS = ["placed", "accepted", "in_kitchen", "ready", "served"];
const STEP_LABEL = { placed: "Placed", paid: "Paid", accepted: "Accepted", in_kitchen: "In kitchen", ready: "Ready", served: "Served" };

function stepIndex(status) {
  if (["pending_payment", "created", "paid"].includes(status)) return 0;
  return STEPS.indexOf(status);
}

export function OrderProgress({status,paymentStatus,paymentMode}) {
  const idx=stepIndex(status);
  const paid=paymentStatus==='paid';
  const paymentLabel=paid?'Paid':paymentStatus==='refunded'?'Refunded':paymentStatus==='partially_refunded'?'Partially refunded':paymentMode==='counter'?'Payment pending · Pay at counter':'Payment pending';
  return <section className="mx-4 -mt-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
    <div data-testid="progress-payment" className={`mb-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${paid?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-800'}`}>
      {paid?<Check aria-hidden="true" className="h-4 w-4"/>:<Receipt aria-hidden="true" className="h-4 w-4"/>}{paymentLabel}
    </div>
    <ol data-testid="order-progress" aria-label="Order preparation progress">
      {STEPS.map((st,i)=><li key={st} className="flex items-center gap-3 py-1.5" data-testid={`order-step-${st}`} aria-current={i===idx?'step':undefined}>
        <span className={`grid h-7 w-7 place-items-center rounded-full border-2 text-xs font-bold transition-colors ${i<=idx?'border-[#FF9D05] bg-[#FF9D05] text-white':'border-stone-300 text-stone-400'}`}>{i<=idx?<Check aria-hidden="true" className="h-4 w-4"/>:i+1}</span>
        <span className={`text-sm ${i===idx?'font-bold':i<idx?'text-stone-700':'text-stone-400'}`}>{STEP_LABEL[st]}</span>
      </li>)}
    </ol>
  </section>;
}

function Feedback({ id, token }) {
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const send = async () => {
    try { await api.post(`/public/orders/${id}/feedback`, { token, food, service, comment }); setDone(true); }
    catch (e) { toast.error(errMsg(e)); if (e.response?.status === 409) setDone(true); }
  };
  if (done) return <p className="mx-4 mt-4 rounded-2xl bg-orange-50 p-4 text-sm font-semibold" data-testid="feedback-done">Thank you for your feedback!</p>;
  const Stars = ({ v, set, k }) => (
    <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`rate-${k}-${n}`} onClick={() => set(n)} className={`text-2xl ${n <= v ? "text-[#FF9D05]" : "text-stone-300"}`}>★</button>)}</div>
  );
  return (
    <section className="mx-4 mt-4 rounded-2xl border border-stone-200 bg-white p-4" data-testid="feedback-form">
      <h3 className="font-bold">How was it?</h3>
      <p className="mt-1 text-xs text-stone-500">Your ratings and comment will appear anonymously in this restaurant’s customer reviews. Please leave out personal details.</p>
      <div className="mt-2 flex items-center justify-between text-sm"><span>Food</span><Stars v={food} set={setFood} k="food" /></div>
      <div className="mt-1 flex items-center justify-between text-sm"><span>Service</span><Stars v={service} set={setService} k="service" /></div>
      <textarea data-testid="feedback-comment" maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything we should know? (optional)" className="mt-2 w-full rounded-xl border border-stone-200 p-3 text-sm" rows={2} />
      <button data-testid="feedback-submit" disabled={!food || !service} onClick={send} className="mt-2 w-full rounded-xl bg-stone-900 py-3 text-sm font-semibold text-white disabled:opacity-40">Send feedback</button>
    </section>
  );
}

export default function OrderStatus() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || localStorage.getItem(`mbp_order_${id}`);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [customerSignedIn,setCustomerSignedIn]=useState(null);
  useEffect(()=>{
    let active=true;
    const refresh=()=>api.get('/customer/me').then(({data})=>{if(active){if(data.customer)localStorage.setItem('mbp_customer_account_saved','true');setCustomerSignedIn(!!data.customer);}}).catch(()=>{if(active)setCustomerSignedIn(null);});
    const onVisible=()=>{if(!document.hidden)refresh();};
    refresh();window.addEventListener('focus',refresh);window.addEventListener('customer-auth-changed',refresh);document.addEventListener('visibilitychange',onVisible);
    return()=>{active=false;window.removeEventListener('focus',refresh);window.removeEventListener('customer-auth-changed',refresh);document.removeEventListener('visibilitychange',onVisible);};
  },[]);

  const loadDetail = () => api.get(`/public/orders/${id}`, { params: { token } }).then((r) => setDetail(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { loadDetail(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = useLive(`order:${id}`, async () => (await api.get(`/public/orders/${id}/status`, { params: { token } })).data, { token, onEvent: loadDetail });

  if (err) return <GuestError msg={err} />;
  if (!detail) return <div className="grid min-h-screen place-items-center bg-[#FAF6F0]"><Loader2 className="h-6 w-6 animate-spin text-[#FF9D05]" /></div>;

  const s = live.data?.status ? live.data : detail.order;
  const o = detail.order;

  const call = async (type) => {
    setBusy(type);
    try {
      const { data } = await api.post(`/public/orders/${id}/service-call`, { token, type });
      toast.success(data.status === "already_open" ? "Staff have already been notified" : type === "call_waiter" ? "A waiter is on the way" : "Your bill is being prepared");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const retry = async () => {
    try { const { data } = await api.post(`/public/orders/${id}/pay`, null, { params: { token } }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#FAF6F0] pb-10" data-testid="order-status-page">
      <header className="grain bg-[#1C1917] px-5 pb-8 pt-7 text-[#FAF6F0]">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-200">{detail.outlet.name} · Table {detail.table.code}</p>
        <h1 className="mt-1 text-4xl font-extrabold tracking-tight">Order #{o.order_no}</h1>
        <p className="mt-2 flex items-center gap-2 text-lg" data-testid="order-status-label">
          {s.status === "ready" ? <ChefHat className="h-5 w-5 text-emerald-400" /> : <span className={`h-2.5 w-2.5 rounded-full ${s.status === "cancelled" ? "bg-red-500" : "animate-pulse bg-[#FF9D05]"}`} />}
          {STATUS_LABEL[s.status] || s.status}
        </p>
        {s.eta_minutes && <p className="mt-3 inline-block rounded-full bg-[#FF9D05] px-4 py-1.5 text-sm font-bold" data-testid="order-eta">Ready in about {s.eta_minutes} min</p>}
        <p className="mt-1 text-xs text-stone-400" data-testid="live-connection">{live.connected ? "Live updates on" : "Updating every few seconds"}</p>
      </header>

      {s.status === "cancelled" ? (
        <div className="m-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="order-cancelled-banner">
          This order was cancelled{o.cancel_reason ? ` (${o.cancel_reason.replace(/_/g, " ")})` : ""}.
          <Link to={`/m/${detail.table.qr_token}`} className="ml-1 font-semibold underline">Back to menu</Link>
        </div>
      ) : (
        <OrderProgress status={s.status} paymentStatus={s.payment_status ?? o.payment_status} paymentMode={o.payment_mode}/>
      )}

      {s.status === "pending_payment" && (
        <div className="mx-4 mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="payment-pending-banner">
          Payment not confirmed yet. Unpaid orders are cancelled after 10 minutes.
          {CUSTOMER_ONLINE_PAYMENTS_ENABLED&&<button data-testid="retry-payment-button" onClick={retry} className="mt-3 block w-full rounded-xl bg-stone-900 py-3 font-semibold text-white">Pay now</button>}
        </div>
      )}

      <div className="mx-4 mt-4 grid grid-cols-2 gap-3">
        <button data-testid="call-waiter-button" disabled={!!busy} onClick={() => call("call_waiter")} className="flex items-center justify-center gap-2 rounded-2xl border-2 border-stone-900 bg-white py-4 text-sm font-bold active:scale-[0.98]">
          <BellRing className="h-4 w-4" /> Call waiter
        </button>
        <button data-testid="request-bill-button" disabled={!!busy} onClick={() => call("request_bill")} className="flex items-center justify-center gap-2 rounded-2xl bg-stone-900 py-4 text-sm font-bold text-white active:scale-[0.98]">
          <Receipt className="h-4 w-4" /> Request bill
        </button>
      </div>

      <section className="mx-4 mt-4 rounded-2xl border border-stone-200 bg-white p-4" data-testid="order-lines">
        {detail.lines.map((l) => (
          <div key={l.id} className={`flex justify-between py-1.5 text-sm ${l.status === "voided" ? "text-stone-400 line-through" : ""}`}>
            <span>{l.qty} × {l.name_snapshot}{l.modifiers?.length ? <span className="block text-xs text-stone-500">{l.modifiers.map((m) => m.name_snapshot).join(", ")}</span> : null}</span>
            <span>{inr(l.unit_price_paise * l.qty)}</span>
          </div>
        ))}
        <div className="mt-2 space-y-1 border-t border-dashed border-stone-300 pt-2 text-sm text-stone-600">
          {o.discount_paise>0&&<div className="flex justify-between text-emerald-700"><span>Discount{o.coupon?` · ${o.coupon.code}`:''}</span><span>− {inr(o.discount_paise)}</span></div>}
          <div className="flex justify-between"><span>CGST + SGST</span><span>{inr(o.tax_paise)}</span></div>
          <div className="flex justify-between text-base font-bold text-stone-900"><span>Total</span><span data-testid="order-total">{inr(o.total_paise)}</span></div>
          <p className="text-xs" data-testid="order-payment-status">Payment: {s.payment_status?.replace(/_/g, " ")} {o.payment_mode === "counter" ? "(pay at counter)" : ""}</p>
        </div>
      </section>

      {s.status === "served" && <Feedback id={id} token={token} />}
      {customerSignedIn===false&&localStorage.getItem('mbp_customer_account_saved')!=='true'&&<section className="mx-4 mt-4 rounded-2xl border border-stone-200 bg-white p-4"><h2 className="font-bold">Keep your orders & rewards</h2><p className="mt-1 text-sm text-stone-500">Create your customer account with phone OTP. Use the same number next time to earn and redeem restaurant loyalty points.</p><Link onClick={()=>localStorage.setItem(`mbp_order_${id}`,token)} to={`/customer?qr=${encodeURIComponent(detail.table.qr_token)}&order=${encodeURIComponent(id)}`} className="mt-3 inline-flex min-h-11 items-center rounded-xl bg-[#FF9D05] px-4 text-sm font-semibold">Save account & rewards</Link></section>}
      {detail.points != null && (
        <p className="mx-4 mt-4 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="guest-points">You have <b>{detail.points}</b> loyalty points{s.points_earned ? ` (+${s.points_earned} from this order)` : ""}. 1 point = ₹1 off your next bill.</p>
      )}
      {detail.bill && (
        <a href={`${BACKEND}${detail.bill.pdf_url}`} target="_blank" rel="noreferrer" data-testid="guest-bill-link"
          className="mx-4 mt-4 flex items-center justify-center gap-2 rounded-2xl border border-stone-300 bg-white py-3 text-sm font-semibold">
          <FileText className="h-4 w-4" /> Download bill {detail.bill.invoice_no}
        </a>
      )}
      <Link to={`/m/${detail.table.qr_token}`} data-testid="order-more-link" className="mt-6 block text-center text-sm font-semibold text-[#FF9D05] underline">Order something else</Link>
    </div>
  );
}
